/*
 * Copyright (c) 2023 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import { Card, Tooltip } from "reactstrap";
import SortableTable from "../../elements/SortableTable";
import { GuestProfileTableLabel } from "./GuestProfile";
import SplitField from "../../provisioning/elements/SplitField";
import { SummaryLabel } from "../../provisioning/elements/ReviewLabels";
import BarChart from "../../dashboard/components/barCharts/BarChart";
import { useState } from "react";

const GuestProfileAutomationHistory = ({
  guest,
}) => {

  const [tooltip, setTooltip] = useState({tooltipId: null})

  // table columns
  const renderTableLabel = ({ index, data, id, value }) => {
    return <GuestProfileTableLabel key={index}>{value}</GuestProfileTableLabel>;
  };

  const renderTableLabelTooltip = ({ index, data, id, value, rowIndex}) => {
    const tooltipId = `render-table-label-${id}-${rowIndex}`
    return (
      <div>
        <GuestProfileTableLabel id={tooltipId} key={tooltipId}>{value}</GuestProfileTableLabel>
        <Tooltip
          placement="top"
          isOpen={tooltip.tooltipId === tooltipId}
          target={tooltipId}
          toggle={() => setTooltip({ tooltipId: tooltipId === tooltip.tooltipId ? null : tooltipId})}
        >
          {value}
        </Tooltip>
      </div>
    )
  }


  // constants
  const automationType = { email: "Email", sms: "SMS", unknown: "Unknown"};
  const automationEvent = { connect: "Connect", idle: "Disconnect", inactive: "Inactive", birthday: "Birthday", broadcast: "Broadcast", unknown: "Unknown"}
  const statusEnum = { success: "Success", error: "Error", notSent: "Not Sent", unknown: "Unknown"};
  const columns = [
    { id: "name", name: "Name", type: "string", render: renderTableLabelTooltip },
    { id: "sendType", name: "Type", type: "enum", enum: automationType, render: renderTableLabel },
    { id: "event", name: "Event", type: "enum", enum: automationEvent, render: renderTableLabel },
    { id: "status", name: "Status", type: "enum", enum: statusEnum, render: renderTableLabel },
    { id: "createdAt", name: "Time", type: "time", render: renderTableLabel },
  ];
  const getAutomationStatus = (event) => {
    switch(event) {
      case 1: //EMAIL_SENT
      case 2: //SMS_SENT
        return "success";
      case 3: //EMAIL_FAILED_TO_SEND
      case 4: //SMS_FAILED_SEND
      case 5: //SEND_ERROR
      case 10: //SENDGRID_SENDER_INVALID
        return "error";
      case 7: //EMAIL_OPT_OUT
      case 8: //SMS_OPT_OUT
      case 9: //AUTOMATION_DISABLED
      case 11: //AUTOMATION_INELIGIBLE
        return "notSent";
      default:
        return "unknown";
    }
  }

  const getBroadcastStatus = (status) => {
    switch(status) {
      case 1: // EMAIL_SENT
      case 2: // SMS_SENT
        return "success";
      case 3: // EMAIL_FAILED_TO_SEND
      case 4: // SMS_FAILED_SEND
      case 5: // SEND_ERROR
      case 8: // SENDGRID_SENDER_INVALID
        return "error";
      case 6: // EMAIL_OPT_OUT
      case 7: // SMS_OPT_OUT
      case 9: // BROADCAST_INELIGIBLE
        return "notSent";
      default:
        return "unknown";
    }
  }

  const marketing = [ ...(guest.automationHistory?.map(item => {
    return {
      name: item.automationName,
      sendType: item.automationType ?? "unknown",
      event: item.triggerType ?? "unknown",
      status: getAutomationStatus(item.eventType),
      createdAt: item.createdAt,
    }
  }) ?? []), ...(guest.broadcastHistory?.map(item => {
    return {
      name: item.broadcastName,
      sendType: item.broadcastType ?? "unknown",
      event: "broadcast",
      status: getBroadcastStatus(item.eventType),
      createdAt: item.createdAt
    } 
  }) ?? [])];  

  // Values

  const dataKeys = [
    { name: "Sent", key: "sent" },
    { name: "Opened", key: "opened" },
    { name: "Clicked", key: "clicked"}
  ];

  const data = guest?.marketing?.data?.map(item => {
    return {
      name: item.name,
      clicked: item.clicked,
      opened: item.opened,
      sent: item.sent
    }
  }) ?? [];

  const props = {
    height: `${300 + Math.random()}px`
  }

  return (
    <Card className="p-2 flex-column d-flex fill-container" style={{ height: "710px", border: "1px"}}>
        <SplitField className="m-4 w-100" >
          <SummaryLabel placeholder="0" title={"Emails Opened"} value={guest.emailsOpened} />
          <SummaryLabel placeholder="0" title={"Emails Clicked"} value={guest.emailsClicked} />
          <SummaryLabel placeholder="0" title={"Emails Sent"} value={guest.emailsSent} />
          <SummaryLabel placeholder="0" title={"SMS Sent"} value={guest.smsSent} />
          <SummaryLabel placeholder="0" title={"SMS Clicked"} value={guest.smsClicked} />
        </SplitField>
        <SortableTable
          data={marketing}
          columns={columns}
          filter={{ string: null }}
          initialSortKey={"createdAt"}
          initialSortAscend={false}
          />
        <div key="guest_automation" className="mt-2" style={{ ...props }}>
          <BarChart
            key="guestAutomationChart"
            title="Automation Trends"
            type="bar"
            data={data}
            dataKeys={dataKeys}
            {...props}
          />
        </div>
    </Card>
  );
};

export default GuestProfileAutomationHistory;